// Typed wrapper for the password-gated `adminDashboard` callable. The dashboard is
// web-only (see App.tsx) and not part of the player app; the password is verified
// server-side, so the data (including otherwise-private picks) is never exposed.
import { httpsCallable } from 'firebase/functions';
import type {
  DuelOutcomeKind,
  DuelSide,
  FixtureStatus,
  ForceUpdateConfig,
  Movement,
  OtaConfig,
  Outcome,
  RoundStatus
} from '@h2h/shared';
import { functions } from './firebase';

export interface AdminMembershipSummary {
  leagueId: string;
  tier: number;
  roundsPlayed: number;
  seasonPoints: number;
}

export interface AdminUserRow {
  userId: string;
  displayName: string;
  username: string | null;
  avatarTone: number;
  createdAt: number;
  /** max(createdAt, latest entry submittedAt) — closest proxy to "last active". */
  lastActiveAt: number;
  friendCount: number;
  leaguesJoined: number;
  picksCount: number;
  hasPushToken: boolean;
  profileComplete: boolean;
  proWaitlist: boolean;
  memberships: AdminMembershipSummary[];
}

export interface AdminPredictionLegRow {
  marketId: string;
  selection: string;
  label: string | null;
}

export interface AdminPredictionRow {
  fixtureId: string;
  label: string;
  homeTeam: string | null;
  awayTeam: string | null;
  /** The fixture's eligible boost catalog (the legs it offered), or null. */
  legCatalog: string[] | null;
  /** The card's 1X2 base. */
  outcome: Outcome;
  /** The multiplier boosts the player stacked. */
  legs: AdminPredictionLegRow[];
  /** The card's signed score, stamped at settlement (null before). */
  cardScore: number | null;
  resultLabel: string | null;
}

/** One cycling duel pick: which rider the player backed, the conviction weight, and
 *  (once settled) the verdict. Cycling entries carry these instead of `predictions`. */
export interface AdminCyclingPickRow {
  duelId: string;
  riderAName: string;
  riderBName: string;
  pick: DuelSide;
  pickedRiderName: string;
  speciality: string | null;
  confidenceWeight: number;
  winner: DuelSide | null;
  outcome: DuelOutcomeKind | null;
  correct: boolean | null;
  points: number | null;
}

export interface AdminEntryRow {
  userId: string;
  displayName: string;
  avatarTone: number;
  leagueId: string;
  leagueName: string;
  roundId: string;
  roundLabel: string;
  roundStatus: RoundStatus;
  tier: number;
  submittedAt: number;
  roundScore: number | null;
  rankInGroup: number | null;
  movement: Movement | null;
  predictions: AdminPredictionRow[];
  /** Cycling entries only: the duel picks (null on football entries). */
  cyclingPicks: AdminCyclingPickRow[] | null;
}

export interface AdminCurrentRound {
  roundId: string;
  label: string;
  status: RoundStatus;
  eligibleMembers: number;
  submitted: number;
  submissionRate: number | null;
}

export interface AdminTierCount {
  tier: number;
  tierName: string;
  count: number;
}

export interface AdminLeagueRow {
  leagueId: string;
  name: string;
  shortCode: string | null;
  sport: string;
  country: string | null;
  season: number;
  format: string;
  currentRoundId: string | null;
  memberCount: number;
  roundsCovered: number;
  entriesCount: number;
  tierCounts: AdminTierCount[];
  currentRound: AdminCurrentRound | null;
}

export interface AdminMiniLeagueRow {
  miniLeagueId: string;
  name: string;
  icon: string;
  color: string;
  leagueId: string;
  leagueName: string;
  ownerId: string;
  ownerName: string;
  memberCount: number;
  inviteCode: string;
  createdAt: number;
}

export interface AdminTrendPoint {
  weekStart: number;
  signups: number;
  picks: number;
}

export interface AdminActivityItem {
  kind: 'signup' | 'join' | 'pick';
  at: number;
  userId: string;
  displayName: string;
  avatarTone: number;
  detail: string;
  leagueId?: string;
}

export interface AdminStats {
  totalUsers: number;
  profileComplete: number;
  totalPicks: number;
  activeLeagues: number;
  totalLeagues: number;
  newUsers7d: number;
  picks7d: number;
  usersWithPicks: number;
  totalMemberships: number;
}

/** Funnel for one in-app nudge. The three outcomes are CTA taps (not completions);
 *  their sum is ≤ `shown` (some sessions end with no recorded exit). */
export interface AdminPromoFunnel {
  shown: number;
  create: number;
  join: number;
  dismiss: number;
}

export interface AdminPromoStats {
  miniLeague: AdminPromoFunnel;
}

export interface AdminDashboardResult {
  ok: true;
  generatedAt: number;
  stats: AdminStats;
  promos: AdminPromoStats;
  users: AdminUserRow[];
  leagues: AdminLeagueRow[];
  entries: AdminEntryRow[];
  activity: AdminActivityItem[];
  trend: AdminTrendPoint[];
  miniLeagues: AdminMiniLeagueRow[];
}

// ── League detail (lazy, fetched per league via adminLeagueDetail) ─────────────

export interface AdminFixtureRow {
  fixtureId: string;
  homeTeam: string;
  awayTeam: string;
  homeCode: string | null;
  awayCode: string | null;
  kickoffAt: number;
  status: FixtureStatus;
  resultLabel: string | null;
  liveLabel: string | null;
}

export interface AdminDivisionStanding {
  rank: number;
  userId: string;
  displayName: string;
  roundScore: number;
  movement: Movement | null;
}

export interface AdminDivisionRow {
  groupId: string;
  tier: number;
  tierName: string;
  memberCount: number;
  standings: AdminDivisionStanding[];
}

export interface AdminRoundDetail {
  roundId: string;
  label: string;
  status: RoundStatus;
  openAt: number;
  firstKickoffAt: number;
  settledAt: number | null;
  picksRequired: number;
  pickMode: string;
  fixtureCount: number;
  entryCount: number;
  fixtures: AdminFixtureRow[];
  divisions: AdminDivisionRow[];
}

export interface AdminLeagueDetailResult {
  ok: true;
  generatedAt: number;
  league: {
    leagueId: string;
    name: string;
    shortCode: string | null;
    sport: string;
    country: string | null;
    season: number;
    format: string;
    currentRoundId: string | null;
  };
  rounds: AdminRoundDetail[];
}

/** Fetch the full admin snapshot. Throws on a wrong password (permission-denied). */
export async function fetchAdminDashboard(password: string): Promise<AdminDashboardResult> {
  const call = httpsCallable<{ password: string }, AdminDashboardResult>(functions, 'adminDashboard');
  return (await call({ password })).data;
}

/** Fetch one league's rounds, fixtures and divisions on demand (heavy; lazy). */
export async function fetchAdminLeagueDetail(
  password: string,
  leagueId: string
): Promise<AdminLeagueDetailResult> {
  const call = httpsCallable<{ password: string; leagueId: string }, AdminLeagueDetailResult>(
    functions,
    'adminLeagueDetail'
  );
  return (await call({ password, leagueId })).data;
}

export interface SetAppConfigResult {
  ok: true;
  forceUpdate: ForceUpdateConfig;
  updatedAt: number;
}

/**
 * Write the app-wide force-update gate (the `config/app` doc). Password-gated and
 * Admin-SDK-written server-side; the input is re-sanitised there, so passing
 * `undefined` for a field clears it. Throws on a wrong password (permission-denied)
 * or an invalid config (invalid-argument — e.g. enabling with no threshold set).
 */
export async function setAppConfig(
  password: string,
  forceUpdate: ForceUpdateConfig
): Promise<SetAppConfigResult> {
  const call = httpsCallable<{ password: string; forceUpdate: ForceUpdateConfig }, SetAppConfigResult>(
    functions,
    'setAppConfig'
  );
  return (await call({ password, forceUpdate })).data;
}

export interface SetOtaConfigResult {
  ok: true;
  ota: OtaConfig;
  updatedAt: number;
}

/**
 * Write the OTA live-update manifest (the `config/ota` doc). Password-gated and
 * Admin-SDK-written server-side; the input is re-sanitised there. The callable
 * preserves the uploaded-bundle history (`available`) — the panel only sets the
 * live pointer + flags (the kill switch, the rollback selection, mandatory,
 * native-build floor, notes). Throws on a wrong password (permission-denied) or
 * an invalid config (invalid-argument — e.g. enabling with no bundle selected).
 */
export async function setOtaConfig(
  password: string,
  ota: OtaConfig
): Promise<SetOtaConfigResult> {
  const call = httpsCallable<{ password: string; ota: OtaConfig }, SetOtaConfigResult>(
    functions,
    'setOtaConfig'
  );
  return (await call({ password, ota })).data;
}

/** Health-check result for one OTA bundle (mirrors the `verifyOtaBundle` callable). */
export interface VerifyOtaBundleResult {
  ok: true;
  version: string | null;
  url: string | null;
  reachable: boolean;
  httpStatus: number | null;
  sizeBytes: number | null;
  expectedChecksum: string | null;
  actualChecksum: string | null;
  checksumMatch: boolean | null;
  error?: string;
}

/**
 * Verify an OTA bundle the players would actually download. With no `version`,
 * checks the live release; otherwise checks that uploaded history version. The
 * callable fetches the stored URL server-side (no browser CORS), recomputes the
 * sha256, and reports reachability + size + checksum match. Read-only and
 * password-gated; throws on a wrong password (permission-denied).
 */
export async function verifyOtaBundle(
  password: string,
  version?: string
): Promise<VerifyOtaBundleResult> {
  const call = httpsCallable<{ password: string; version?: string }, VerifyOtaBundleResult>(
    functions,
    'verifyOtaBundle'
  );
  return (await call({ password, version })).data;
}

// ── Manual push broadcasts (sendBroadcast) ─────────────────────────────────────

/** Who a broadcast targets. Mirrors the server's `Audience` union. */
export type BroadcastAudience =
  | { kind: 'all' }
  | { kind: 'league'; leagueId: string }
  | { kind: 'miniLeague'; miniLeagueId: string };

export interface BroadcastInput {
  title: string;
  body: string;
  /** In-app destination for a tap (any route, e.g. "/leagues", "/pro"). Optional. */
  path?: string;
  /** Optional CTA button label for the in-app feed card. */
  ctaLabel?: string;
  audience: BroadcastAudience;
  /** Pin the feed item to a league's bell (defaults to a league audience's league). */
  leagueId?: string;
  /** Stable per-message id (regenerated when the compose form changes). A real send
   *  claims it server-side so a retry of the same message can't double-push. */
  broadcastId?: string;
  /** When true (the default), nothing is written/sent — returns the audience preview. */
  dryRun: boolean;
}

export interface BroadcastResult {
  ok: true;
  dryRun: boolean;
  /** True when this broadcast was already sent — nothing was re-delivered. */
  duplicate?: boolean;
  audienceLabel: string;
  audienceSize: number;
  recipients: number;
  optedOut: number;
  withDevices: number;
  deviceCount: number;
  feedWritten: number;
  pushDelivered: number;
  sample: { title: string; body: string; path: string | null; ctaLabel: string | null };
}

/**
 * Send (or preview, when `dryRun`) a manual push + in-app notification to a chosen
 * audience. Password-gated server-side. Throws on a wrong password (permission-denied)
 * or invalid input (invalid-argument — e.g. empty title, non-"/" path).
 */
export async function sendBroadcast(
  password: string,
  input: BroadcastInput
): Promise<BroadcastResult> {
  const call = httpsCallable<{ password: string } & BroadcastInput, BroadcastResult>(
    functions,
    'sendBroadcast'
  );
  return (await call({ password, ...input })).data;
}

/**
 * True only when the callable rejected the password itself. Any other failure
 * (server crash, network, internal error) is NOT a wrong password and must not
 * be reported as one — otherwise a real 500 masquerades as a bad password and
 * hides the actual bug.
 */
export function isWrongPasswordError(err: unknown): boolean {
  const code = (err as { code?: string } | null)?.code;
  return code === 'functions/permission-denied' || code === 'functions/unauthenticated';
}
